import { useState } from "react";
import api from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { useRefresh } from "@/hooks/useRefresh";
import { peso, num, fmtDate } from "@/lib/format";
import { MovementDialog } from "@/components/MovementDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";

const REASON_BADGE = {
  spoilage: "bg-red-100 text-red-700 hover:bg-red-100",
  breakage: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  "over-prep": "bg-blue-100 text-blue-800 hover:bg-blue-100",
  "staff meal": "bg-slate-100 text-slate-700 hover:bg-slate-100",
};

export default function Wastage() {
  const { selected } = useBranch();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  useRefresh(() => api.get("/wastage", { params: { branch_id: selected } }).then((r) => setRows(r.data)), [selected]);

  const total = rows.reduce((s, r) => s + r.value, 0);

  return (
    <div className="space-y-4" data-testid="wastage-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Wastage log</h1>
          <p className="text-sm text-muted-foreground">{rows.length} entries · <span className="text-red-600 font-medium">{peso(total)} lost</span></p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="log-wastage-btn"><Plus className="h-4 w-4 mr-1" /> Log wastage</Button>
      </div>
      <div className="border rounded-xl bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr className="border-b">
              <th className="py-2 px-3">Date</th><th className="py-2 px-3">Item</th><th className="py-2 px-3">Branch</th>
              <th className="py-2 px-3 text-right">Qty</th><th className="py-2 px-3">Reason</th>
              <th className="py-2 px-3 text-right">Value lost</th><th className="py-2 px-3">Logged by</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.movement_id} className="border-b last:border-0 hover:bg-slate-50">
                <td className="py-2 px-3 text-muted-foreground">{fmtDate(r.created_at)}</td>
                <td className="py-2 px-3 font-medium">{r.item_name}</td>
                <td className="py-2 px-3">{r.branch_name}</td>
                <td className="py-2 px-3 text-right">{num(Math.abs(r.qty))} {r.unit}</td>
                <td className="py-2 px-3"><Badge className={REASON_BADGE[r.reason] || "bg-slate-100 text-slate-700"}>{r.reason || "—"}</Badge></td>
                <td className="py-2 px-3 text-right text-red-600 font-medium">{peso(r.value)}</td>
                <td className="py-2 px-3 text-muted-foreground">{r.user_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <MovementDialog open={open} onOpenChange={setOpen} defaultType="wastage" lockType />
    </div>
  );
}
